import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// Public read-only client view. Rendered at /c/{token} — no auth required.
// The consultant mints this link from the Report page ("Share with client")
// and hands it to their CIO / procurement counterpart.
//
// The token is a 256-bit URL-safe secret bound to a specific assessment and
// carries an expiry date (default 30 days). Bad or expired tokens produce
// a friendly message. Nothing about the consultant's account is exposed.

const CATEGORY_LABELS = {
    license_visibility: "License Visibility",
    user_segmentation: "User Segmentation",
    governance: "Governance",
    cost_control: "Cost Control",
    future_readiness: "Future Readiness",
    procurement_readiness: "Procurement Readiness",
};
const CAT_ORDER = [
    "license_visibility",
    "user_segmentation",
    "governance",
    "cost_control",
    "future_readiness",
    "procurement_readiness",
];

function rag(score) {
    if (score == null) return "Amber";
    if (score < 40) return "Red";
    if (score < 70) return "Amber";
    return "Green";
}

function maturity(score) {
    if (score <= 25) return "Initial";
    if (score <= 50) return "Developing";
    if (score <= 75) return "Managed";
    return "Optimized";
}

// A minimal but faithful markdown renderer for the shared view. Kept in
// step with Report.jsx behaviour (no dangerouslySetInnerHTML — the AI
// output cannot inject arbitrary HTML).
function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split(/\r?\n/);
    const out = [];
    let k = 0;
    let inList = false;
    let listBuf = [];
    const flushList = () => {
        if (listBuf.length > 0) {
            out.push(
                <ul key={`ul-${k++}`}>
                    {listBuf.map((li, i) => (
                        <li key={i}>{li}</li>
                    ))}
                </ul>
            );
            listBuf = [];
        }
        inList = false;
    };
    for (let raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flushList();
            continue;
        }
        if (line.startsWith("### ")) {
            flushList();
            out.push(<h3 key={k++}>{line.slice(4)}</h3>);
        } else if (line.startsWith("## ")) {
            flushList();
            out.push(<h2 key={k++}>{line.slice(3)}</h2>);
        } else if (line.startsWith("# ")) {
            flushList();
            out.push(<h1 key={k++}>{line.slice(2)}</h1>);
        } else if (/^\s*[-*]\s+/.test(line)) {
            if (!inList) inList = true;
            listBuf.push(line.replace(/^\s*[-*]\s+/, ""));
        } else {
            flushList();
            out.push(<p key={k++}>{line}</p>);
        }
    }
    flushList();
    return out;
}

export default function ClientReport() {
    const { token } = useParams();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/report/${token}`);
                setData(data);
            } catch (e) {
                const msg = e?.response?.data?.detail || e?.message || "Unable to load report.";
                setErr(String(msg));
            } finally {
                setLoading(false);
            }
        })();
    }, [token, location.pathname]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6 py-16">
                <div className="text-[#c2c2c2] uppercase tracking-widest text-sm" data-testid="client-report-loading">
                    Loading report…
                </div>
            </div>
        );
    }
    if (err) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6 py-16">
                <div
                    className="w-full max-w-lg border border-[#c95151] bg-[#1a0e0e] p-6 md:p-8 text-center"
                    data-testid="client-report-error"
                >
                    <div className="overline text-[#c95151]">This link isn&apos;t working</div>
                    <p className="mt-3 text-sm text-[#e2e2e2] leading-relaxed">{err}</p>
                    <p className="mt-4 text-xs text-[#9a9a9a]">
                        Please ask the advisor who shared this with you to reissue the link.
                    </p>
                </div>
            </div>
        );
    }

    const overall = data.scores?.overall ?? 0;
    const overallRag = rag(overall);
    const overallMaturity = maturity(overall);
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/report/${token}/pdf`;

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white">
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12" data-testid="client-report-page">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="overline text-[#7a7a7a]" data-testid="client-report-brand">
                        SmartNote2Self · License Risk Scan · Read-only client view
                    </div>
                    <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="client-report-pdf-link"
                    >
                        Download PDF
                    </a>
                </div>

                <section className="border border-[#2a2a2a] bg-[#141414] p-8 md:p-10">
                    <div className="flex justify-between items-start gap-4 flex-wrap">
                        <div className="overline">Confidential briefing</div>
                        {data.share_expires_at && (
                            <div
                                className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#c2c2c2] border border-[#2a2a2a] px-2 py-1"
                                data-testid="client-report-expires"
                            >
                                Link expires{" "}
                                {new Date(data.share_expires_at).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </div>
                        )}
                    </div>
                    <h1
                        className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mt-4"
                        data-testid="client-report-org"
                    >
                        {data.organization_name}
                    </h1>
                    <div className="text-[#c2c2c2] mt-3 text-sm">
                        {data.industry} · {data.user_count} users · {data.agreement_type}
                    </div>

                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        <div className="bg-[#0a0a0a] p-5">
                            <div className="overline">Overall</div>
                            <div className="font-mono text-4xl mt-1" data-testid="client-report-overall">
                                {overall}
                            </div>
                        </div>
                        <div className="bg-[#0a0a0a] p-5">
                            <div className="overline">Risk</div>
                            <div className="mt-3 flex items-center h-10">
                                <span
                                    className={`rag-dot rag-dot-lg rag-dot-${overallRag}`}
                                    aria-label={`Overall RAG: ${overallRag}`}
                                />
                            </div>
                        </div>
                        <div className="bg-[#0a0a0a] p-5">
                            <div className="overline">Maturity</div>
                            <div className="font-display text-2xl mt-1">{overallMaturity}</div>
                        </div>
                        <div className="bg-[#0a0a0a] p-5">
                            <div className="overline">Confidence</div>
                            <div className="font-display text-2xl mt-1">{data.confidence || "Low"}</div>
                        </div>
                    </div>

                    <div className="mt-px grid grid-cols-2 md:grid-cols-6 gap-px bg-[#2a2a2a] border border-[#2a2a2a] border-t-0">
                        {CAT_ORDER.map((c) => {
                            const s = data.scores?.[c] ?? 0;
                            const r = rag(s);
                            return (
                                <div
                                    key={c}
                                    className="bg-[#0a0a0a] p-4 flex flex-col"
                                    data-testid={`client-score-${c}`}
                                >
                                    <div
                                        className="text-[12px] uppercase tracking-widest text-[#9a9a9a] leading-tight min-h-[2.4em]"
                                        style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                        }}
                                    >
                                        {CATEGORY_LABELS[c]}
                                    </div>
                                    <div className="font-mono text-2xl mt-3 flex items-center gap-3 leading-none">
                                        <span>{s}</span>
                                        <span
                                            className={`rag-dot rag-dot-lg rag-dot-${r}`}
                                            aria-label={`${CATEGORY_LABELS[c]} RAG: ${r}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <article className="report-prose mt-10" data-testid="client-report-body">
                    {renderMarkdown(data.report_md || "")}
                </article>

                <div className="mt-12 border-t border-[#2a2a2a] pt-6 text-xs text-[#7a7a7a]" data-testid="client-report-footer">
                    Prepared by your independent advisor using the SmartNote2Self License Risk Scan.
                    This is a read-only briefing — nothing you view here changes the advisor&apos;s
                    working copy. Questions belong with the advisor who sent you this link.
                </div>
            </div>
        </div>
    );
}
